import { Box } from '@mui/system'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close'
import { Fragment, useContext, useEffect, useRef, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { Button, Dialog, IconButton, Tooltip, Typography } from '@mui/material'

import DialogTable from './dialogTable'
import { RoleInfo } from '@models/role.model'
import useTableView, { Sorting } from '@hooks/useTableView'
import { OptionsContext } from '@contexts/Options'
import Header from '@components/configurations/header'
import { ReactComponent as ProjectAccessSettingIcon } from '@assets/sideMenuIcon/project_access_setting.svg'
import useIsMounted from '@hooks/useIsMounted'
import { groupBy, merge } from 'lodash'
import UserService from '@services/users/user.service'
import { sortByName } from '@utils/sortByName'
import useAPIFetch from '@hooks/useAPIFetch'
import { useMeta } from '@contexts/Meta'
import { KeyValPair } from '@models/common.model'
import DialogContent from './dialog/dialogContent'
import { hasPermission } from '@utils/permission_helper'
import { PermissionCodeEnum } from '@models/permission'
import { AuthContext } from '@contexts/Auth'

const FormBox = styled(Box)(() => ({
	display: 'flex',
	flexDirection: 'column',
	gap: '12px',
}))

const SimpleDialog = ({
	open,
	onConfirm,
	onClose,
	userName,
	list,
	options,
	selectedGroups,
	setSelectedGroups,
	handleSearchRolesByProject,
	handleInputSearch,
	setInputValueSearched,
	inputValueSearched,
}: {
	open: boolean
	onConfirm: () => void
	onClose: () => void
	userName: string
	list: RoleInfo[] | null
	options: KeyValPair[]
	selectedGroups: string[]
	setSelectedGroups: any
	handleSearchRolesByProject: any
	handleInputSearch: any
	setInputValueSearched: any
	inputValueSearched: string
}) => {
	return (
		<Dialog onClose={onClose} open={open} fullWidth maxWidth="md" sx={{ padding: '0' }}>
			<Box
				display="flex"
				justifyContent={'space-between'}
				alignItems="center"
				sx={{ padding: '16px 25px 16px 25px', backgroundColor: '#F6F6F6', color: '#707070' }}
			>
				<Typography variant="h3">Edit {userName} Roles</Typography>
				<IconButton onClick={onClose}>
					<CloseIcon />
				</IconButton>
			</Box>
			{/* <DialogTable /> */}
			<DialogContent
				list={list}
				options={options}
				selectedGroups={selectedGroups}
				setSelectedGroups={setSelectedGroups}
				handleSearchRolesByProject={handleSearchRolesByProject}
				handleInputSearch={handleInputSearch}
				setInputValueSearched={setInputValueSearched}
				inputValueSearched={inputValueSearched}
			/>
			<Box
				display="flex"
				justifyContent={'flex-end'}
				alignItems="center"
				sx={{ padding: '16px', backgroundColor: '#F6F6F6' }}
				gap="12px"
			>
				<Button variant="contained" color="defaultButton" onClick={onConfirm}>
					Confirm
				</Button>
				<Button variant="contained" color="error" onClick={onClose}>
					Cancel
				</Button>
			</Box>
		</Dialog>
	)
}

export default function PermissionSetting() {
	const { t } = useTranslation()
	const [, { setMeta }] = useMeta()

	const { control, setValue, getValues } = useFormContext()
	const roleIds = useWatch({ control, name: 'role_ids' })

	const { state, dispatch } = useContext(OptionsContext)
	const { state: authState } = useContext(AuthContext)

	const [open, setOpen] = useState(false)
	const [dialogListOptions, setDialogListOption] = useState<{
		// list: RoleInfo[]
		list: any
		options: KeyValPair[]
	}>({ list: null, options: [] })
	const refMounted = useRef(true)

	const [roleListPerProject, setRoleListPerProject] = useState<any>(null)

	const [selectedGroups, setSelectedGroups] = useState<any>([])
	const [initalSelectedGroups, setInitalSelectedGroups] = useState<any>([])

	const [inputValueSearched, setInputValueSearched] = useState<string>('')
	const [projectValueSearched, setProjectValueSearched] = useState<string[]>([])

	useEffect(() => {
		reload()
	}, [roleIds])

	const getMyProfileRoles = async () => {
		const getRolesRequest = {
			// role_ids: {
			// 	operator: '!=',
			// 	value: roleIds,
			// },
			// active: {
			// 	operator: '=',
			// 	value: [true]
			// },
			// sort: {
			// 	by: "string",
			// 	order_by: "string"
			// },
			with_product_modules: true,
		}

		const rolelist = await UserService.GetMyProfileRoles(getRolesRequest)
		console.debug(
			'🚀  getDialogDetails  rolelist',
			rolelist.data.sort((a: any, b: any): any => {
				if (a.project_id < b.project_id) {
					return -1
				}
				if (a.project_id > b.project_id) {
					return 1
				}
				return 0
			}),
		)
		const rolesPerProject: Record<string, RoleInfo[]> = groupBy(rolelist.data, 'project_id')
		const _roleListPerProject = []
		for (const [key, value] of Object.entries(rolesPerProject)) {
			_roleListPerProject.push({ title: key, contents: value })
		}

		setRoleListPerProject(_roleListPerProject)
	}

	useEffect(() => {
		getMyProfileRoles()
	}, [])

	const getDialogDetails = async (searchedRoleListPerProject: any) => {
		const getRolesRequest = {
			role_ids: {
				operator: '!=',
				value: roleIds,
			},
			// active: {
			// 	operator: '=',
			// 	value: [true]
			// },
			// sort: {
			// 	by: "string",
			// 	order_by: "string"
			// },
			with_product_modules: true,
		}

		const getMyProfileProjectList = {
			with_product_modules: true,
		}

		const projectInfomation = await UserService.GetMyProfileProjectList(getMyProfileProjectList)
		console.debug(
			'🚀  getDialogDetails  projectInfomation',
			projectInfomation.data.sort((a: any, b: any): any => {
				if (a.project_id < b.project_id) {
					return -1
				}
				if (a.project_id > b.project_id) {
					return 1
				}
				return 0
			}),
		)

		let projectInfomationWithRoles = []
		if (searchedRoleListPerProject) {
			projectInfomationWithRoles = projectInfomation.data?.map((project: any) => {
				for (let i = 0; i < searchedRoleListPerProject.length; i++) {
					if (searchedRoleListPerProject[i].title === project.project_id) {
						project.roles = searchedRoleListPerProject[i].contents
						return project
					}
				}
			})
		} else {
			projectInfomationWithRoles = projectInfomation.data?.map((project: any) => {
				for (let i = 0; i < roleListPerProject.length; i++) {
					if (roleListPerProject[i].title === project.project_id) {
						project.roles = roleListPerProject[i].contents
						return project
					}
				}
			})
		}

		const _projectInfomationWithRoles = projectInfomationWithRoles.filter((elem: any) => {
			return elem !== undefined
		})

		const roleListPerProjectModified = _projectInfomationWithRoles.map((project: any) => {
			const rolesPerProject = project.roles.map((role: any) => {
				console.log('project.product_modules', project.product_modules)

				const productsPerRole = project.product_modules.map((product: any) => {
					console.debug(product)
					const productId = product?.id

					const filterProductIsActive = (productId: string) => {
						const foundProduct = role.project_subs.filter((product: any) => {
							return product.item_id === productId
						})
						return !!foundProduct.length
					}
					return {
						...product,
						productCode: product.code,
						isActive: filterProductIsActive(productId),
					}
				})

				console.log('productsPerRole', productsPerRole)

				return {
					id: role.id,
					roleName: role.display_name,
					code: role.code,
					baseSystemField: role.base_system_field,
					inheritedRoleIds: role.inherited_role_ids,
					isAdmin: role.is_admin,
					orgIds: role.org_ids,
					productAccess: productsPerRole,
					roleProfileIds: role.role_profile_ids,
					roleRightsList: role.role_rights_list,
				}
			})

			return {
				code: project?.code,
				title: project?.display_name,
				id: project?.project_id,
				org_ids: project.org_ids,
				contents: rolesPerProject,
			}
		})

		let projectOptions: KeyValPair[] = []
		projectInfomation?.data?.forEach((x) => {
			let p = state.project?.find((y) => y.key === x.project_id)
			if (p) {
				projectOptions.push(p)
			}
		})

		setDialogListOption({
			list: roleListPerProjectModified,
			options: sortByName(projectOptions),
		})
	}

	const handleInputSearch = async (inputValue: string) => {
		console.debug('🚀  handleInputSearch  projectValueSearched', projectValueSearched)

		const payload: any = {
			project_ids: { operator: '=', value: projectValueSearched },
			search: [inputValue.trim()],
			skip_project_search: true,
		}

		if (projectValueSearched.length === 0) {
			delete payload.project_ids
		}

		const rolelist = await UserService.GetMyProfileRoles(payload)

		const rolesPerProject: Record<string, RoleInfo[]> = groupBy(rolelist.data, 'project_id')
		const searchedRoleListPerProject = []
		for (const [key, value] of Object.entries(rolesPerProject)) {
			searchedRoleListPerProject.push({ title: key, contents: value })
		}

		getDialogDetails(searchedRoleListPerProject)
	}

	const handleSearchRolesByProject = async (projectIds: any) => {
		console.debug('🚀  handleSearchRolesByProject  inputValueSearched', inputValueSearched)
		setProjectValueSearched(projectIds)
		const payload: any = {
			project_ids: { operator: '=', value: projectIds },
			search: [inputValueSearched],
			skip_project_search: true,
		}

		if (inputValueSearched === '') {
			delete payload.search
		}

		const rolelist = await UserService.GetMyProfileRoles(payload)

		const rolesPerProject: Record<string, RoleInfo[]> = groupBy(rolelist.data, 'project_id')
		const searchedRoleListPerProject = []
		for (const [key, value] of Object.entries(rolesPerProject)) {
			searchedRoleListPerProject.push({ title: key, contents: value })
		}

		getDialogDetails(searchedRoleListPerProject)
	}

	const handleClickOpen = async () => {
		getDialogDetails(null)
		handleOpenDialog()
	}

	const handleOpenDialog = () => {
		setOpen(true)
	}

	const handleConfirm = () => {
		setOpen(false)
		setValue('role_ids', selectedGroups)
	}

	const handleClose = () => {
		setOpen(false)
		setSelectedGroups([...initalSelectedGroups])
		setInputValueSearched('')
		setProjectValueSearched([])
	}

	const [TableView, reload] = useTableView<RoleInfo>({
		columns: [
			{
				key: 'display_name',
				desc: t('Role Name'),
				disableSort: false,
				renderCell: (params: RoleInfo) => {
					return (
						<Fragment>
							<Typography variant="body2">{params.display_name}</Typography>
						</Fragment>
					)
				},
			},
			{
				key: 'project_subs',
				desc: t('Projects'),
				disableSort: false,
				renderCell: (params: RoleInfo) => {
					const products = params?.project_subs
						?.map((x) => {
							switch (x.item_type) {
								case 'product':
									return state.product?.find((product) => product.key === x.item_id)?.value
								case 'module':
									return state.module?.find((module) => module.key === x.item_id)?.value
							}
						})
						.join(', \n')
					const result =
						state.project?.find((x) => x.key === params?.project_id)?.value + ' - ' + products ?? ''
					return (
						<Fragment>
							<Tooltip title={result}>
								<Typography variant="body2">{result}</Typography>
							</Tooltip>
						</Fragment>
					)
				},
			},
			{
				key: 'org_ids',
				desc: t('Organization'),
				disableSort: false,
				renderCell: (params: RoleInfo) => {
					const result = params?.org_ids?.map((org) => {
						return state.organization?.find((x) => x.key === org)?.value
					})
					return (
						<Fragment>
							<Typography variant="body2">
								{result?.filter((x: string) => x?.length > 0 && x !== undefined)?.join(',')}
							</Typography>
						</Fragment>
					)
				},
			},
			{
				key: 'id',
				desc: t('Function Buttons'),
				renderCell: (params: RoleInfo) => {
					return (
						<Tooltip title="Remove">
							<IconButton
								onClick={() => {
									let new_value = roleIds.filter((role: any) => role !== params.id)
									setValue('role_ids', new_value)

									let _index = [...selectedGroups].indexOf(params.id)
									if (_index !== -1) {
										let _selectedGroups = [...selectedGroups]
										_selectedGroups.splice(_index, 1)
										setSelectedGroups(_selectedGroups)
										setInitalSelectedGroups(_selectedGroups)
									}
								}}
							>
								<CloseIcon />
							</IconButton>
						</Tooltip>
					)
				},
			},
		],
		reloadCallback: async (
			page?: number,
			page_size?: number,
			filter?: any,
			search?: any,
			sorting?: Sorting,
		) => {
			if (refMounted.current && roleIds && roleIds.length > 0) {
				let _filter =
					roleIds.length > 0 ? { ...filter, role_ids: { operator: '=', value: roleIds } } : filter
				let req = merge(_filter, {
					pagination: {
						page: page ?? 0,
						page_size: page_size,
					},
					search: search,
					sort: sorting,
				})

				let roleData = await UserService.GetMyProfileRoles(req)

				const filterOptions = req
				delete filterOptions.pagination
				delete filterOptions.search
				delete filterOptions.sort
				delete filterOptions.active
				let optionsList = await UserService.GetRolesOptions(filterOptions)
				let new_criterion = [
					{
						key: 'display_name',
						name: t('User Role'),
						type: 'StringFilter',
					},
					{
						key: 'project_ids',
						name: t('Projects'),
						type: 'SelectionFilter',
						options: sortByName(
							state.project?.filter((elem: any) => {
								return optionsList?.project_ids?.includes(elem.key)
							}),
						),
					},
					{
						key: 'active',
						name: t('Active'),
						type: 'ActiveFilter',
					},
				]
				let results: { data: RoleInfo[]; total_count: number; criterion: any } = {
					...roleData,
					criterion: new_criterion,
				}

				const _ = results.data.map((elem: any) => {
					return elem.id
				})
				setSelectedGroups(_)
				setInitalSelectedGroups(_)
				return results
			}
		},
		toolbar: true,
		mountedRef: refMounted,
		tableHeight: '330px',
		rowsPerPage: 25,
		additionButton: (
			<Button
				variant="contained"
				onClick={handleClickOpen}
				color="defaultButton"
				disabled={state.project === undefined || !roleListPerProject ? true : false}
			>
				{t('Add User Role')}
			</Button>
		),
		criterion: [
			{
				key: 'role_ids',
				name: t('User Role'),
				type: 'SelectionFilter',
				options: state.role,
			},
			{
				key: 'active',
				name: t('Active'),
				type: 'ActiveFilter',
			},
		],
		defaultFilterCriteria: { active: { operator: '=', value: [true] } },
		renderNoResult: false,
	})

	return (
		<>
			<Header
				title={'Permission Setting'}
				icon={<ProjectAccessSettingIcon style={{ width: '25px' }} />}
			/>
			<FormBox>
				<SimpleDialog
					open={open}
					onConfirm={handleConfirm}
					onClose={handleClose}
					userName={getValues('first_name') + ' ' + getValues('last_name')}
					list={dialogListOptions.list}
					options={dialogListOptions.options}
					selectedGroups={selectedGroups}
					setSelectedGroups={setSelectedGroups}
					handleSearchRolesByProject={handleSearchRolesByProject}
					handleInputSearch={handleInputSearch}
					setInputValueSearched={setInputValueSearched}
					inputValueSearched={inputValueSearched}
				/>
				<TableView />
			</FormBox>
		</>
	)
}
